if (!customElements.get('bulk-order-form')) {
  customElements.define(
    'bulk-order-form',
    class BulkOrderForm extends HTMLElement {
      constructor() {
        super();
        this.quantities = new Map();
        this.stickyBar = this.querySelector('[data-sticky-bar]');
        this.itemCountEl = this.querySelector('[data-items-count]');
        this.subtotalEl = this.querySelector('[data-subtotal]');
        this.addBtn = this.querySelector('[data-add-to-cart]');
        this.searchInput = this.querySelector('[data-bulk-order-search]');
        this.filterSelect = this.querySelector('[data-bulk-order-filter]');
        this.collectionFilter = this.querySelector('[data-bulk-order-collection-filter]');
        this.currency = this.dataset.currency || 'USD';
        this.sectionId = this.dataset.sectionId;
        this.debounceTimer = null;

        this.init();
      }

      init() {
        this.bindQuantityInputs();
        this.bindExpandButtons();
        this.bindSearch();
        this.bindFilter();
        this.bindCollectionFilter();
        this.bindAddToCart();
        this.initStickyBar();
        this.restorePendingQuantities();
      }

      bindQuantityInputs() {
        this.querySelectorAll('.factor2-bulk-order__qty-input').forEach((input) => {
          input.addEventListener('input', this.handleQuantityChange.bind(this));
          input.addEventListener('change', this.handleQuantityChange.bind(this));
        });
      }

      bindExpandButtons() {
        this.querySelectorAll('.factor2-bulk-order__expand-btn, .factor2-bulk-order__card-expand').forEach((btn) => {
          btn.addEventListener('click', this.toggleVariants.bind(this));
        });
      }

      bindSearch() {
        if (!this.searchInput) return;

        this.searchInput.addEventListener('input', (e) => {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => {
            this.handleSearch(e.target.value);
          }, 300);
        });
      }

      bindFilter() {
        if (!this.filterSelect) return;

        this.filterSelect.addEventListener('change', (e) => {
          this.handleFilter(e.target.value);
        });
      }

      bindCollectionFilter() {
        if (!this.collectionFilter) return;

        this.collectionFilter.addEventListener('change', (e) => {
          this.handleCollectionChange(e.target.value);
        });
      }

      async handleCollectionChange(collectionHandle) {
        if (this.quantities.size > 0) {
          const confirmMessage =
            window.bulkOrderTranslations?.confirm_collection_change ||
            'Changing collection will clear your current selections. Continue?';
          if (!confirm(confirmMessage)) {
            const currentCollection = new URLSearchParams(window.location.search).get('collection');
            if (currentCollection) {
              this.collectionFilter.value = currentCollection;
            }
            return;
          }
          this.resetForm();
        }

        this.setTableLoading(true);

        try {
          const url = new URL(window.location.href);
          url.searchParams.set('collection', collectionHandle);
          url.searchParams.delete('page');

          const sectionUrl = `${url.pathname}?${url.searchParams.toString()}&section_id=${this.sectionId}`;

          const response = await fetch(sectionUrl);
          if (!response.ok) throw new Error('Failed to fetch collection');

          const html = await response.text();
          this.updateSectionContent(html);

          window.history.pushState({}, '', url.toString());
        } catch (error) {
          console.error('Failed to load collection:', error);
        } finally {
          this.setTableLoading(false);
        }
      }

      updateSectionContent(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newSection = doc.querySelector(`#BulkOrder-${this.sectionId}`);

        if (!newSection) return;

        const newTableWrapper = newSection.querySelector('.factor2-bulk-order__table-wrapper');
        const newCards = newSection.querySelector('.factor2-bulk-order__cards');
        const newTypeFilter = newSection.querySelector('[data-bulk-order-filter="type"]');

        const currentTableWrapper = this.querySelector('.factor2-bulk-order__table-wrapper');
        const currentCards = this.querySelector('.factor2-bulk-order__cards');
        const currentTypeFilter = this.querySelector('[data-bulk-order-filter="type"]');

        if (newTableWrapper && currentTableWrapper) {
          currentTableWrapper.innerHTML = newTableWrapper.innerHTML;
        }
        if (newCards && currentCards) {
          currentCards.innerHTML = newCards.innerHTML;
        }
        if (newTypeFilter && currentTypeFilter) {
          currentTypeFilter.innerHTML = newTypeFilter.innerHTML;
        }

        this.bindQuantityInputs();
        this.bindExpandButtons();

        if (this.searchInput) {
          this.searchInput.value = '';
        }
        if (this.filterSelect) {
          this.filterSelect.value = '';
        }
      }

      setTableLoading(loading) {
        const tableWrapper = this.querySelector('.factor2-bulk-order__table-wrapper');
        const cards = this.querySelector('.factor2-bulk-order__cards');

        if (tableWrapper) {
          tableWrapper.classList.toggle('loading', loading);
        }
        if (cards) {
          cards.classList.toggle('loading', loading);
        }
      }

      bindAddToCart() {
        if (!this.addBtn) return;

        this.addBtn.addEventListener('click', this.handleAddToCart.bind(this));
      }

      initStickyBar() {
        // Sticky bar is now position: sticky, always visible at bottom of form
      }

      handleQuantityChange(event) {
        const input = event.target;
        const variantId = input.dataset.variantId;
        const price = parseInt(input.dataset.price) || 0;
        const quantity = parseInt(input.value) || 0;

        if (quantity > 0) {
          this.quantities.set(variantId, { quantity, price });
          input.classList.add('has-value');
        } else {
          this.quantities.delete(variantId);
          input.classList.remove('has-value');
        }

        this.updateStickyBar();
      }

      toggleVariants(event) {
        const btn = event.currentTarget;
        const productId = btn.dataset.productId;
        const isExpanded = btn.getAttribute('aria-expanded') === 'true';

        btn.setAttribute('aria-expanded', !isExpanded);

        const variantRows = this.querySelectorAll(`.factor2-bulk-order__variant-row[data-product-id="${productId}"]`);
        variantRows.forEach((row) => {
          row.classList.toggle('collapsed', isExpanded);
        });

        const cardVariants = btn
          .closest('.factor2-bulk-order__card')
          ?.querySelector('.factor2-bulk-order__card-variants');
        if (cardVariants) {
          cardVariants.classList.toggle('expanded', !isExpanded);
        }
      }

      handleSearch(query) {
        query = query.toLowerCase().trim();
        let visibleCount = 0;

        this.querySelectorAll('.factor2-bulk-order__product-row').forEach((row) => {
          const sku = row.dataset.sku || '';
          const name = row.dataset.name || '';
          const matches = !query || sku.includes(query) || name.includes(query);

          row.classList.toggle('hidden', !matches);

          if (matches) {
            visibleCount++;
          }

          if (row.classList.contains('has-variants')) {
            const productId = row.dataset.productId;
            const variantRows = this.querySelectorAll(
              `.factor2-bulk-order__variant-row[data-product-id="${productId}"]`,
            );
            variantRows.forEach((vr) => {
              const variantSku = vr.dataset.sku || '';
              const variantName = vr.dataset.name || '';
              const variantMatches = !query || variantSku.includes(query) || variantName.includes(query);

              if (!matches && !variantMatches) {
                vr.classList.add('hidden');
              } else {
                vr.classList.remove('hidden');
              }
            });
          }
        });

        this.querySelectorAll('.factor2-bulk-order__card').forEach((card) => {
          const sku = card.dataset.sku || '';
          const name = card.dataset.name || '';
          const matches = !query || sku.includes(query) || name.includes(query);

          card.classList.toggle('hidden', !matches);

          if (matches) {
            visibleCount++;
          }
        });

        const noResults = this.querySelector('.factor2-bulk-order__no-results');
        if (noResults) {
          noResults.classList.toggle('visible', visibleCount === 0 && query);
        }
      }

      handleFilter(filterValue) {
        this.querySelectorAll('.factor2-bulk-order__product-row').forEach((row) => {
          const rowType = row.dataset.type || '';
          const matches = !filterValue || rowType === filterValue;

          row.classList.toggle('hidden', !matches);

          if (row.classList.contains('has-variants')) {
            const productId = row.dataset.productId;
            const variantRows = this.querySelectorAll(
              `.factor2-bulk-order__variant-row[data-product-id="${productId}"]`,
            );
            variantRows.forEach((vr) => {
              vr.classList.toggle('hidden', !matches);
            });
          }
        });

        this.querySelectorAll('.factor2-bulk-order__card').forEach((card) => {
          const cardType = card.dataset.type || '';
          const matches = !filterValue || cardType === filterValue;

          card.classList.toggle('hidden', !matches);
        });
      }

      updateStickyBar() {
        const itemCount = this.quantities.size;
        let subtotal = 0;
        let hasAffidavitProducts = false;

        const customerLoggedIn = this.dataset.customerLoggedIn === 'true';
        const validAffidavitProducts = (this.dataset.validAffidavitProducts || '').split(',').filter(Boolean);

        this.quantities.forEach(({ quantity, price }, variantId) => {
          subtotal += price * quantity;

          const input = this.querySelector(`[data-variant-id="${variantId}"]`);
          const productRow = input?.closest('[data-product-id]');
          if (productRow) {
            const productId = productRow.dataset.productId;
            const requiresAffidavit = productRow.dataset.requiresAffidavit === 'true';
            if (requiresAffidavit && !validAffidavitProducts.includes(productId)) {
              hasAffidavitProducts = true;
            }
          }
        });

        if (this.itemCountEl) {
          const itemText = itemCount === 1 ? 'item' : 'items';
          this.itemCountEl.textContent = `${itemCount} ${itemText} selected`;
        }

        if (this.subtotalEl) {
          this.subtotalEl.textContent = `Subtotal: ${this.formatMoney(subtotal)}`;
        }

        if (this.addBtn) {
          this.addBtn.disabled = itemCount === 0;
          const btnLabel = this.addBtn.querySelector('span');
          if (btnLabel) {
            if (hasAffidavitProducts) {
              btnLabel.textContent = (
                window.bulkOrderTranslations?.sign_and_add_to_cart || 'Sign Affidavit & Add to Cart'
              ).replace(/&amp;/g, '&');
            } else {
              btnLabel.textContent = (window.bulkOrderTranslations?.add_to_cart || 'Add to Cart').replace(
                /&amp;/g,
                '&',
              );
            }
          }
        }
      }

      formatMoney(cents) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.currency,
        }).format(cents / 100);
      }

      async handleAddToCart() {
        if (this.quantities.size === 0) return;

        const customerLoggedIn = this.dataset.customerLoggedIn === 'true';
        const validAffidavitProducts = (this.dataset.validAffidavitProducts || '').split(',').filter(Boolean);
        const productsNeedingAffidavit = [];

        this.quantities.forEach((data, variantId) => {
          const input = this.querySelector(`[data-variant-id="${variantId}"]`);
          const productRow = input?.closest('[data-product-id]');
          if (!productRow) return;

          const productId = productRow.dataset.productId;
          const requiresAffidavit = productRow.dataset.requiresAffidavit === 'true';

          if (requiresAffidavit && !validAffidavitProducts.includes(productId)) {
            const sku =
              productRow.dataset.sku || input?.closest('[data-sku]')?.dataset.sku || productRow.dataset.variantId || '';
            productsNeedingAffidavit.push({
              productId,
              productName: productRow.dataset.name || 'Product',
              sku: sku.toUpperCase(),
            });
          }
        });

        if (productsNeedingAffidavit.length > 0) {
          if (!customerLoggedIn) {
            this.showLoginRequired();
            return;
          }
          this.showAffidavitModal(productsNeedingAffidavit);
          return;
        }

        const updates = {};
        this.quantities.forEach(({ quantity }, variantId) => {
          updates[variantId] = quantity;
        });

        this.setLoading(true);

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              items: Object.entries(updates).map(([id, quantity]) => ({
                id: parseInt(id),
                quantity,
              })),
            }),
          });

          const data = await response.json();

          if (response.ok) {
            this.showSuccess();
            this.resetForm();

            this.updateCartUI();

            if (typeof publish === 'function') {
              publish('cart-update', {
                source: 'bulk-order',
                cartData: data,
              });
            }
          } else {
            throw new Error(data.description || 'Failed to add items to cart');
          }
        } catch (error) {
          console.error('Bulk order error:', error);
          this.showError(error.message);
        } finally {
          this.setLoading(false);
        }
      }

      async updateCartUI() {
        try {
          const cartResponse = await fetch(
            `${window.routes.cart_url}?sections=cart-icon-bubble,cart-drawer,cart-notification`,
          );
          const cartData = await cartResponse.json();

          if (cartData.sections) {
            Object.entries(cartData.sections).forEach(([sectionId, html]) => {
              const sectionElement = document.getElementById(`shopify-section-${sectionId}`);
              if (sectionElement) {
                sectionElement.innerHTML = new DOMParser()
                  .parseFromString(html, 'text/html')
                  .querySelector(`#shopify-section-${sectionId}`).innerHTML;
              }
            });
          }
        } catch (error) {
          console.error('Failed to update cart UI:', error);
        }
      }

      setLoading(loading) {
        if (this.addBtn) {
          this.addBtn.classList.toggle('loading', loading);
          this.addBtn.disabled = loading;

          const label = this.addBtn.querySelector('span');
          if (label) {
            if (loading) {
              this.addBtn.dataset.originalText = label.textContent;
              label.textContent = window.bulkOrderTranslations?.adding || 'Adding...';
            } else {
              label.textContent =
                this.addBtn.dataset.originalText || window.bulkOrderTranslations?.add_to_cart || 'Add to Cart';
            }
          }
        }
      }

      showSuccess() {
        const notification = document.createElement('div');
        notification.className = 'factor2-bulk-order__notification factor2-bulk-order__notification--success';
        notification.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>Items added to cart successfully!</span>
        `;
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #10B981;
          color: white;
          padding: 16px 24px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-family: var(--font-body, 'Inter', sans-serif);
          font-size: 14px;
          font-weight: 500;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          z-index: 10000;
          animation: slideIn 0.3s ease;
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
          notification.style.animation = 'slideOut 0.3s ease forwards';
          setTimeout(() => notification.remove(), 300);
        }, 3000);
      }

      showError(message) {
        const notification = document.createElement('div');
        notification.className = 'factor2-bulk-order__notification factor2-bulk-order__notification--error';
        notification.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <span>${message}</span>
        `;
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #EF4444;
          color: white;
          padding: 16px 24px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-family: var(--font-body, 'Inter', sans-serif);
          font-size: 14px;
          font-weight: 500;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          z-index: 10000;
          animation: slideIn 0.3s ease;
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
          notification.style.animation = 'slideOut 0.3s ease forwards';
          setTimeout(() => notification.remove(), 300);
        }, 5000);
      }

      resetForm() {
        this.quantities.clear();

        this.querySelectorAll('.factor2-bulk-order__qty-input').forEach((input) => {
          input.value = 0;
          input.classList.remove('has-value');
        });

        this.updateStickyBar();
      }

      showLoginRequired() {
        const message =
          window.bulkOrderTranslations?.login_required_message ||
          'Please log in to purchase products that require affidavits.';
        this.showError(message);

        const quantitiesToSave = {};
        this.quantities.forEach((data, variantId) => {
          quantitiesToSave[variantId] = data.quantity;
        });
        sessionStorage.setItem('bulkOrderPendingQuantities', JSON.stringify(quantitiesToSave));
        sessionStorage.setItem('bulkOrderReturnUrl', window.location.href);

        const loginUrl = this.dataset.loginUrl || window.routes?.storefront_login_url || '/account/login';
        setTimeout(() => {
          window.location.href = loginUrl;
        }, 2000);
      }

      showAffidavitModal(products) {
        const quantitiesToSave = {};
        this.quantities.forEach((data, variantId) => {
          quantitiesToSave[variantId] = data.quantity;
        });
        sessionStorage.setItem('bulkOrderPendingQuantities', JSON.stringify(quantitiesToSave));
        sessionStorage.setItem('bulkOrderReturnUrl', window.location.href);

        const firstProduct = products[0];

        const event = new CustomEvent('openAffidavitModal', {
          detail: {
            productId: firstProduct.productId,
            productCodes: firstProduct.sku,
          },
        });
        document.dispatchEvent(event);

        if (products.length > 1) {
          const remainingMsg = `${products.length - 1} more product(s) will require affidavits after this one.`;
          console.log(remainingMsg);
        }
      }

      restorePendingQuantities() {
        const saved = sessionStorage.getItem('bulkOrderPendingQuantities');
        if (!saved) return;

        try {
          const quantities = JSON.parse(saved);
          let restoredCount = 0;
          const expandProductIds = new Set();

          Object.entries(quantities).forEach(([variantId, qty]) => {
            const inputs = this.querySelectorAll(`[data-variant-id="${variantId}"]`);
            if (inputs.length > 0 && qty > 0) {
              inputs.forEach((input) => {
                input.value = qty;
              });
              this.handleQuantityChange({ target: inputs[0] });
              restoredCount++;

              const productRow = inputs[0].closest('[data-product-id]');
              if (productRow) {
                expandProductIds.add(productRow.dataset.productId);
              }
            }
          });

          expandProductIds.forEach((productId) => {
            const variantRows = this.querySelectorAll(
              `.factor2-bulk-order__variant-row[data-product-id="${productId}"]`,
            );
            variantRows.forEach((row) => row.classList.remove('collapsed'));

            const expandBtn = this.querySelector(`.factor2-bulk-order__expand-btn[data-product-id="${productId}"]`);
            if (expandBtn) {
              expandBtn.setAttribute('aria-expanded', 'true');
            }

            const cardVariants = this.querySelector(
              `.factor2-bulk-order__card[data-product-id="${productId}"] .factor2-bulk-order__card-variants`,
            );
            if (cardVariants) {
              cardVariants.classList.add('expanded');
            }
          });

          sessionStorage.removeItem('bulkOrderPendingQuantities');
          sessionStorage.removeItem('bulkOrderReturnUrl');

          if (restoredCount > 0) {
            this.showRestoredNotification();
          }
        } catch (e) {
          console.error('Failed to restore pending quantities:', e);
          sessionStorage.removeItem('bulkOrderPendingQuantities');
          sessionStorage.removeItem('bulkOrderReturnUrl');
        }
      }

      showRestoredNotification() {
        const message =
          window.bulkOrderTranslations?.quantities_restored ||
          'Your selections have been restored. Click "Add to Cart" to continue.';

        const notification = document.createElement('div');
        notification.className = 'factor2-bulk-order__notification factor2-bulk-order__notification--info';
        notification.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <span>${message}</span>
        `;
        notification.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #0A3C63;
          color: white;
          padding: 16px 24px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 12px;
          font-family: var(--font-body, 'Inter', sans-serif);
          font-size: 14px;
          font-weight: 500;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          z-index: 10000;
          animation: slideIn 0.3s ease;
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
          notification.style.animation = 'slideOut 0.3s ease forwards';
          setTimeout(() => notification.remove(), 300);
        }, 5000);
      }
    },
  );
}

const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
  .factor2-bulk-order__product-row.hidden,
  .factor2-bulk-order__variant-row.hidden {
    display: none;
  }
`;
document.head.appendChild(style);
